import {
  faArrowUp,
  faCopy,
  faLock,
  faPen,
  faPlus,
  faTrashCan,
  faWandMagicSparkles,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { CloseButton } from "../components/CloseButton";
import { SideBar } from "../components/SideBar";
import { useAccount } from "../state";
import { getCsrfToken } from "../state/csrf";
import { SkillBadges } from "../workspace/chat/SkillBadges";
import { fromServer, toServer } from "../workspace/chat/skillForm";
import { FieldInput } from "../workspace/chat/SkillPanel";
import {
  canAdjust,
  draftFrom,
  emptyBinding,
  emptyField,
  FIELD_MODES,
  FIELD_TYPES,
  FieldDraft,
  FieldMode,
  fieldMode,
  fieldName,
  hasOptions,
  INSTRUCTION_MAX,
  LibrarySkill,
  openFields,
  setFieldMode,
  setFieldValue,
  SkillAction,
  SkillBinding,
  SkillDraft,
  toPayload,
} from "./skillDraft";

const json = (method: string, body?: any): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
  ...(body ? { body: JSON.stringify(body) } : {}),
});

const input =
  "w-full rounded-md px-2 py-1 text-sm bg-white border border-sidebar-menu-border placeholder:text-gray-400";

const FieldRow = ({
  field,
  taken,
  onChange,
  onRemove,
  onUp,
}: {
  field: FieldDraft;
  taken: string[];
  onChange: (field: FieldDraft) => void;
  onRemove: () => void;
  onUp?: () => void;
}) => (
  <li className="rounded border border-gray-200 bg-white p-2">
    <div className="flex gap-1.5">
      <input
        value={field.label}
        placeholder="Label, bv. Gewenste schaal"
        onChange={(event) => {
          const label = event.target.value;
          // De naam volgt het label, zolang hij een afgeleide naam is.
          onChange({ ...field, label, name: fieldName(label, taken) });
        }}
        className={input}
      />
      <select
        value={field.type}
        onChange={(event) => onChange({ ...field, type: event.target.value as FieldDraft["type"] })}
        className="rounded-md border border-sidebar-menu-border bg-white px-1 text-sm"
      >
        {FIELD_TYPES.map((type) => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
      {onUp && (
        <button type="button" onClick={onUp} title="Omhoog" className="px-1 text-gray-400">
          <FontAwesomeIcon icon={faArrowUp} size="xs" />
        </button>
      )}
      <button
        type="button"
        onClick={onRemove}
        title="Veld verwijderen"
        className="px-1 text-gray-300 hover:text-red-600"
      >
        <FontAwesomeIcon icon={faXmark} size="sm" />
      </button>
    </div>
    {hasOptions(field.type) && (
      <textarea
        value={field.options}
        rows={3}
        placeholder="Keuzes, één per regel"
        onChange={(event) => onChange({ ...field, options: event.target.value })}
        className={`${input} mt-1.5`}
      />
    )}
    <input
      value={field.description}
      placeholder="Uitleg voor het model (optioneel)"
      onChange={(event) => onChange({ ...field, description: event.target.value })}
      className={`${input} mt-1.5 text-xs`}
    />
    <label className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
      <input
        type="checkbox"
        checked={field.required}
        onChange={(event) => onChange({ ...field, required: event.target.checked })}
      />
      Verplicht
      <span className="ml-auto font-mono text-[10px] text-gray-400">{field.name}</span>
    </label>
  </li>
);

/**
 * Per veld van een actie: laten, voorvullen of vastzetten, met de waarde. Een
 * skill zonder instructie toont ook wat zijn formulier dan nog vraagt.
 */
const BindingEditor = ({
  action,
  binding,
  showForm,
  onChange,
}: {
  action: SkillAction;
  binding: SkillBinding;
  showForm: boolean;
  onChange: (binding: SkillBinding) => void;
}) => {
  const asked = openFields(action.fields, binding).map((field) => field.label);
  const adjusted = Object.keys(binding.defaults).length + Object.keys(binding.fixed).length;
  return (
    <div className="ml-5 mt-1 text-xs">
      {showForm && (
        <p className="text-gray-500">
          Het formulier vraagt:{" "}
          {asked.length ? asked.join(", ") : "niets; Start voert hem meteen uit"}.
        </p>
      )}
      <details className="mt-0.5">
        <summary className="cursor-pointer text-dark-green">
          Velden aanpassen{adjusted ? ` (${adjusted} aangepast)` : ""}
        </summary>
        <ul className="mt-1 space-y-1.5">
          {action.fields.map((field) => {
            const mode = fieldMode(binding, field.name);
            const value =
              mode === "fixed" ? binding.fixed[field.name] : binding.defaults[field.name];
            return (
              <li key={field.name} className="rounded border border-gray-200 bg-white p-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="flex-1">{field.label}</span>
                  <select
                    value={mode}
                    disabled={!canAdjust(field)}
                    title={canAdjust(field) ? undefined : "Een perceel kies je per project"}
                    onChange={(event) =>
                      onChange(setFieldMode(binding, field, event.target.value as FieldMode))
                    }
                    className="rounded-md border border-sidebar-menu-border bg-white px-1"
                  >
                    {FIELD_MODES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {mode !== "keep" && (
                  <div className="mt-1">
                    <FieldInput
                      field={field}
                      value={fromServer(field, value)}
                      parcels={[]}
                      onChange={(changed) =>
                        onChange(setFieldValue(binding, field, toServer(field, changed)))
                      }
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </details>
    </div>
  );
};

const Editor = ({
  initial,
  actions,
  onSaved,
  onCancel,
}: {
  initial: SkillDraft;
  actions: SkillAction[];
  onSaved: () => void;
  onCancel: () => void;
}) => {
  const [draft, setDraft] = useState(initial);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const names = draft.fields.map((field) => field.name);
  const withInstruction = draft.instruction.trim() !== "";
  const setField = (index: number, field: FieldDraft) =>
    setDraft({ ...draft, fields: draft.fields.map((f, i) => (i === index ? field : f)) });

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const response = await fetch(
      draft.id ? `/api/skills/${draft.id}` : "/api/skills",
      json(draft.id ? "PUT" : "POST", toPayload(draft))
    );
    setBusy(false);
    if (response.ok) return onSaved();
    const body = await response.json().catch(() => ({}));
    setErrors(body.errors ?? [body.detail || "Opslaan is niet gelukt."]);
  };

  return (
    <form onSubmit={save} className="space-y-3 text-sm">
      <label className="block">
        <span className="font-semibold">Naam</span>
        <input
          value={draft.label}
          required
          maxLength={100}
          onChange={(event) => setDraft({ ...draft, label: event.target.value })}
          placeholder="Bv. Zonnepark-check"
          className={input}
        />
      </label>
      <label className="block">
        <span className="font-semibold">Wanneer aanbieden</span>
        <textarea
          value={draft.when}
          rows={2}
          maxLength={1000}
          onChange={(event) => setDraft({ ...draft, when: event.target.value })}
          placeholder="Voor het model: bij welke vragen past deze skill?"
          className={input}
        />
      </label>
      <label className="block">
        <span className="font-semibold">Instructie</span>
        <textarea
          value={draft.instruction}
          rows={8}
          maxLength={INSTRUCTION_MAX}
          onChange={(event) => setDraft({ ...draft, instruction: event.target.value })}
          placeholder="Wat het model doet als de skill start. Verwijs naar de velden bij hun label."
          className={input}
        />
        <span className="text-[11px] text-gray-500">
          {draft.instruction.length} / {INSTRUCTION_MAX} tekens.{" "}
          {withInstruction
            ? "Start is een beurt van het model, met de acties hieronder."
            : "Leeg: de skill voert één actie uit met een formulier, zonder model."}
        </span>
      </label>

      {withInstruction && (
        <div>
          <p className="font-semibold">Velden</p>
          <p className="text-xs text-gray-500">Wat het formulier vraagt vóór de skill start.</p>
          <ul className="mt-1.5 space-y-1.5">
            {draft.fields.map((field, index) => (
              <FieldRow
                key={field.uid}
                field={field}
                taken={names.filter((_, i) => i !== index)}
                onChange={(changed) => setField(index, changed)}
                onRemove={() =>
                  setDraft({ ...draft, fields: draft.fields.filter((_, i) => i !== index) })
                }
                onUp={
                  index > 0
                    ? () => {
                        const fields = [...draft.fields];
                        [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
                        setDraft({ ...draft, fields });
                      }
                    : undefined
                }
              />
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setDraft({ ...draft, fields: [...draft.fields, emptyField(names)] })}
            className="mt-1.5 text-xs text-dark-green underline"
          >
            <FontAwesomeIcon icon={faPlus} size="xs" className="mr-1" />
            Veld toevoegen
          </button>
        </div>
      )}

      <div>
        <p className="font-semibold">{withInstruction ? "Acties" : "Actie"}</p>
        <p className="text-xs text-gray-500">
          {withInstruction
            ? "Wat het model tijdens de skill mag laten doen."
            : "Wat de skill uitvoert; het formulier komt uit deze actie."}
        </p>
        {actions.map((action) => {
          const index = draft.actions.findIndex((item) => item.action === action.name);
          const chosen = index >= 0;
          const unavailable = !withInstruction && action.needsAnswer;
          const choose = (on: boolean) =>
            setDraft({
              ...draft,
              actions: !on
                ? draft.actions.filter((item) => item.action !== action.name)
                : withInstruction
                  ? [...draft.actions, emptyBinding(action.name)]
                  : [emptyBinding(action.name)],
            });
          return (
            <div key={action.name} className="mt-1">
              <label className={`flex items-start gap-1.5 ${unavailable ? "opacity-50" : ""}`}>
                <input
                  type={withInstruction ? "checkbox" : "radio"}
                  name="skill-action"
                  className="mt-1"
                  checked={chosen}
                  disabled={unavailable}
                  onChange={(event) => choose(event.target.checked)}
                />
                <span>
                  {action.label}
                  {action.paid && (
                    <span className="ml-1.5 rounded bg-gold-brown/20 px-1 text-[10px] font-bold uppercase text-gold-brown">
                      betaald
                    </span>
                  )}
                  <span className="block text-xs text-gray-500">
                    {action.description}
                    {unavailable &&
                      " Alleen in een skill met instructie: hij gebruikt het antwoord."}
                  </span>
                </span>
              </label>
              {chosen && (
                <BindingEditor
                  action={action}
                  binding={draft.actions[index]}
                  showForm={!withInstruction}
                  onChange={(binding) =>
                    setDraft({
                      ...draft,
                      actions: draft.actions.map((item, i) => (i === index ? binding : item)),
                    })
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      {errors.length > 0 && (
        <ul className="list-disc pl-4 text-xs text-red-600">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-bright-yellow px-3 py-1.5 font-bold hover:bg-yellow disabled:opacity-50"
        >
          {busy ? "Opslaan…" : "Opslaan"}
        </button>
        <button type="button" onClick={onCancel} className="px-2 text-gray-500">
          Annuleren
        </button>
      </div>
    </form>
  );
};

const SkillCard = ({
  skill,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  skill: LibrarySkill;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) => (
  <li className="mb-2 rounded border border-gray-100 bg-white p-3 shadow">
    <div className="flex items-center gap-2">
      <FontAwesomeIcon
        icon={skill.system ? faLock : faWandMagicSparkles}
        size="sm"
        className="text-gray-400"
        title={skill.system ? "Systeemskill: niet te bewerken" : undefined}
      />
      <span className="flex-1 font-semibold">
        {skill.label}
        {/* Hier zegt het slotje al wat van het systeem is; alleen de kosten erbij. */}
        <SkillBadges skill={{ paid: skill.paid }} />
      </span>
      {!skill.system && (
        <button type="button" onClick={onEdit} title="Bewerken" className="px-1 text-gray-500">
          <FontAwesomeIcon icon={faPen} size="sm" />
        </button>
      )}
      <button type="button" onClick={onDuplicate} title="Dupliceren" className="px-1 text-gray-500">
        <FontAwesomeIcon icon={faCopy} size="sm" />
      </button>
      {!skill.system && (
        <button
          type="button"
          onClick={onDelete}
          title="Verwijderen"
          className="px-1 text-gray-300 hover:text-red-600"
        >
          <FontAwesomeIcon icon={faTrashCan} size="sm" />
        </button>
      )}
    </div>
    {skill.when && <p className="mt-1 line-clamp-2 text-xs text-gray-600">{skill.when}</p>}
    {!skill.system && skill.updatedBy && (
      <p className="mt-1 text-[11px] text-gray-400">Laatst bewerkt door {skill.updatedBy}</p>
    )}
  </li>
);

/**
 * De skills van de organisatie: de systeemskills met een slotje, en de eigen
 * skills om te maken, te bewerken en te verwijderen. Een systeemskill is te
 * dupliceren als basis voor een eigen skill.
 */
export const SkillsPage = () => {
  const authenticated = useAccount((s) => Boolean(s.user));
  const [skills, setSkills] = useState<LibrarySkill[]>([]);
  const [actions, setActions] = useState<SkillAction[]>([]);
  const [editing, setEditing] = useState<SkillDraft>();

  const refresh = useCallback(() => {
    fetch("/api/skills")
      .then((response) => (response.ok ? response.json() : []))
      .then(setSkills);
  }, []);
  useEffect(() => {
    if (!authenticated) return;
    refresh();
    const controller = new AbortController();
    fetch("/api/skills/actions", { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : []))
      .then(setActions)
      .catch(() => undefined);
    return () => controller.abort();
  }, [authenticated, refresh]);

  if (!authenticated) return null;

  const remove = async (skill: LibrarySkill) => {
    if (!window.confirm(`Skill "${skill.label}" verwijderen?`)) return;
    await fetch(`/api/skills/${skill.id}`, json("DELETE"));
    refresh();
  };

  return (
    <SideBar>
      <div className="p-6">
        <CloseButton className="text-black hover:text-gray hover:bg-black" />
        <h1 className="mb-1 text-2xl font-semibold">Skills</h1>
        <p className="mb-4 text-sm text-gray-600">
          Taken die je in de chat start met / of de toverstaf. Zonder instructie voert een skill één
          actie uit, met jouw voorgevulde en vaste waarden. Met een instructie is hij een opdracht
          aan het model, met de acties die het daarbij mag gebruiken.
        </p>
        {editing ? (
          <Editor
            initial={editing}
            actions={actions}
            onSaved={() => {
              setEditing(undefined);
              refresh();
            }}
            onCancel={() => setEditing(undefined)}
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() => setEditing(draftFrom())}
              className="mb-3 rounded-md bg-green px-3 py-1.5 text-sm text-white hover:bg-dark-green"
            >
              <FontAwesomeIcon icon={faPlus} className="pr-1" />
              Nieuwe skill
            </button>
            <ul>
              {skills.map((skill) => (
                <SkillCard
                  key={skill.id ?? skill.name}
                  skill={skill}
                  onEdit={() => setEditing(draftFrom(skill))}
                  onDuplicate={() =>
                    setEditing({
                      ...draftFrom(skill),
                      id: undefined,
                      label: `${skill.label} (kopie)`,
                    })
                  }
                  onDelete={() => remove(skill)}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </SideBar>
  );
};
